import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment';
import * as React from 'react';

import Button from '../../atoms/Button';
import FilterControl from '../../atoms/FilterControl';
import Overlay from '../../atoms/Overlay';
import Surface from '../../atoms/Surface';
import MaskedInput from '../../molecules/MaskedInput';
import { OnChangeDateProps } from '../../molecules/PresetDateSelector';
import DatePicker from '../DatePicker';

import styles from './MonthFilter.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'> {
  className?: string;
  style?: React.CSSProperties;
  isRangePicker?: boolean;
  from: string;
  to: string;
  onSubmit: (from: string, to: string) => void;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
}

// Define date input parameters
const inputFormat = 'MMM/YYYY';
const mask = 'aaa/YYYY';
const replacement = {
  a: /[a-zA-Z]/,
  Y: /\d/,
};

const MonthFilter: React.FunctionComponent<Props> = ({
  className,
  style,
  isRangePicker,
  from,
  to,
  onSubmit,
  minDate,
  maxDate,
  ...htmlElementProps
}) => {
  // Define states
  const [show, setShow] = React.useState(false);
  const [target, setTarget] = React.useState(null);

  // Global date states
  const [startDate, setStartDate] = React.useState(from);
  const [endDate, setEndDate] = React.useState(to);

  // Input date states
  const [inputDate, setInputDate] = React.useState(
    moment(from).format(inputFormat),
  );
  const [inputStartDate, setInputStartDate] = React.useState(
    moment(from).format(inputFormat),
  );
  const [inputEndDate, setInputEndDate] = React.useState(
    moment(to).format(inputFormat),
  );

  /**
   * Handle a date change made from the date picker or a preset date
   */
  const handleIntervalChange = React.useCallback((vars: OnChangeDateProps) => {
    setInputDate(moment(vars.from).format(inputFormat));
    setInputStartDate(moment(vars.from).format(inputFormat));
    setInputEndDate(moment(vars.to).format(inputFormat));
    setStartDate(vars.from);
    setEndDate(vars.to);
  }, []);

  /**
   * Ensure form can be submitted
   */
  const canSubmit = React.useMemo(
    () => moment(endDate).diff(moment(startDate), 'days') >= 0,
    [startDate, endDate],
  );

  /**
   * Handle submit button click
   */
  const handleSubmit = React.useCallback(() => {
    if (canSubmit) {
      onSubmit(startDate, endDate);
      setShow(false);
    }
  }, [canSubmit, onSubmit, startDate, endDate]);

  /**
   * Handle cancel button click
   */
  const handleCancel = React.useCallback(() => {
    setStartDate(from);
    setEndDate(to);
    setShow(false);
  }, [from, to]);

  /** Sync internal state with external state */
  React.useEffect(() => {
    setStartDate(from);
    setEndDate(to);
    setInputDate(moment(from).format(inputFormat));
    setInputStartDate(moment(from).format(inputFormat));
    setInputEndDate(moment(to).format(inputFormat));
  }, [from, to]);

  return (
    <div className={styles.wrapper} {...htmlElementProps}>
      <FilterControl
        className={className}
        style={style}
        ref={setTarget as React.Ref<HTMLDivElement>}
        prepend={faCalendarDays}
        placeholder={
          isRangePicker
            ? `${moment(from).format(inputFormat)} - ${moment(to).format(
                inputFormat,
              )}`
            : moment(from).format(inputFormat)
        }
        onClick={() => setShow(!show)}
        readOnly
      />

      <Overlay
        target={target}
        placement="bottom-end"
        show={show}
        onHide={() => setShow(false)}
      >
        <Surface
          className="d-flex flex-column"
          backgroundColor="grayLight3"
          padding="sm"
        >
          <Surface
            backgroundColor="grayLight1"
            padding="sm"
            style={{ margin: '1rem 0' }}
          >
            <DatePicker
              isRangePicker={isRangePicker}
              from={startDate}
              to={endDate}
              onChange={handleIntervalChange}
              minDate={minDate}
              maxDate={maxDate}
              view="year"
              maxDetail="year"
            />
          </Surface>
          {isRangePicker ? (
            <div className={styles['date-input-wrapper']}>
              <MaskedInput
                name="from"
                label="Mês/Ano inicial"
                value={inputStartDate}
                mask={mask}
                replacement={replacement}
                style={{ width: '194px' }}
                disabled
              />
              <MaskedInput
                name="to"
                label="Mês/Ano final"
                value={inputEndDate}
                mask={mask}
                replacement={replacement}
                style={{ width: '194px' }}
                disabled
              />
            </div>
          ) : (
            <div className={styles['date-input-wrapper']}>
              <MaskedInput
                name="date"
                label="Mês/Ano"
                value={inputDate}
                mask={mask}
                replacement={replacement}
                style={{ width: '407px' }}
                disabled
              />
            </div>
          )}

          <div className="d-flex flex-justify-end">
            <Button variant="link" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button disabled={!canSubmit} onClick={handleSubmit}>
              Aplicar
            </Button>
          </div>
        </Surface>
      </Overlay>
    </div>
  );
};

export default MonthFilter;
